export enum DeviceType {
    BOARD = 'board',
    TPP = 'TPP'
}

export interface Device {
    id: string;
    name: string;
    description: string;
    platform: string;
    image: string;
    deviceType: DeviceType;
    runtimes: string[];
    capabilities?: string[];
    firmwareVersion?: string;
    uploadMethods?: string[];
    meta?: any;
}

export enum SettingType {
    STRING = 'string',
    NAME = 'name',
    OPTION = 'option',
    NUMBER = 'number',
    PIN = 'pin',
    HIDDEN = 'hidden',
    GROUP = 'group',
    PASSWORD = 'password',
    BUFFER = 'buffer',
    ARRAY = 'array',
    BOOLEAN = 'boolean',
    STG = 'STG',
    IP = 'IP',
    COLOR = 'COLOR',
    MULTISELECT = 'multi',
    MULTISELECT_OPTION = 'multi_option',
    DYNAMIC = 'dynamic',
    FILE = 'file',
    DATA = 'data',
    CRON = 'cron',
    CODE = 'code',
    STRINGVARIABLE = 'stringvariable',
    CONSOLE_PREVIEW = 'console_preview',
}

export enum TibboDataType {
    CHAR = 'char',
    BYTE = 'byte',
    INTEGER = 'integer',
    SHORT = 'short',
    WORD = 'word',
    LONG = 'long',
    DWORD = 'dword',
    REAL = 'real',
    FLOAT = 'float',
    STRING = 'string',
    BOOLEAN = 'boolean',
    CUSTOM = 'custom',
    JSON = 'json',
    SETTING = 'setting',
    DATETIME = 'datetime',
    TIME = 'time',
}

export interface SettingOption {
    name: string;
    value: string;
}

export interface SettingUIControl {
    CONTROL: string;
    OPTIONS?: any[];
}

export interface SettingDefinition {
    SETTINGID: string;
    NAME: string;
    SETTINGTYPE: string;
    DATATYPE: string;
    MIN?: number;
    MAX?: number;
    DEFAULT?: string | number;
}

export interface Setting {
    id: string;
    name: string;
    settingType: SettingType;
    required?: boolean;
    default: any;
    group?: string;
    options?: Array<SettingOption>;
    readonly?: boolean;
    /** For linking nested features */
    refId?: string;
    value?: any;
    showChildren?: boolean;
    helpText?: string;
    min?: number;
    max?: number;
    setting?: SettingDefinition;
    UIControl?: SettingUIControl;
    linkToSetting?: boolean;
    /** For restricting pins or creating settings with correct configs */
    meta?: any;
    /** For linking to setting or data variable */
    dataType?: TibboDataType;
    controlType?: SettingControlType;
    settingsState?: SettingState;
    capabilityRequirement?: string;
}

export interface Widget {
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
    type: string;
    variable?: string;
    name?: string;
    values?: number;
    label?: string;
    displayLabel?: string;
    min?: number;
    max?: number;
    decimalPlaces?: number;
    bounds?: number[];
    hasInput?: boolean;
    onValue?: number;
    offValue?: number;
    onLabel?: string;
    offLabel?: string;
    isOn?: boolean;
    isHorizontal?: boolean;
    customFn?: string;
    [key: string]: any;
}

export enum SettingState {
    ENABLED = 'enabled',
    DISABLED = 'disabled',
    HIDDEN = 'hidden'
}

export enum SettingControlType {
    TABS = 'tabs',
    WIDGETS = 'widgets',
    EXPRESSIONS = 'expressions',
}


export enum BlockType {
    CODE,
    LOCATION
}

export enum IOMode {
    IO = '0',
    OUTPUT = '1',
    INPUT = '2',
}

export enum IOLinesMode {
    Standard = '0',
    Button = '1',
    Interrupt = '2',
    Output = '3'
}

export interface CodeBlock {
    location: string;
    blockType?: BlockType;
    id?: string;
    template: string;
}

export interface SocketConfig {
    socket: string;
    pins: Array<string>;
    pin_numbers?: Array<string>;
    accept: Array<string>;
    reject: Array<string>;
    serial?: string;
    interrupt?: string;
}

export interface TibboBasicEnum {
    enum: string;
    value: string;
}

export interface PinPeripheral {
    peripheralType: string;
    peripheralPort: string;
    peripheralPinName: string;
    num?: string;
}

export interface PinConfig {
    num: string;
    function: string;
    description: string;
    constant?: string;
    interrupt?: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    peripherals: PinPeripheral[];
}

export enum FeatureType {
    FEATURE = 'feature',
    LIBRARY = 'library',
    PERIPHERAL = 'peripheral',
    TIBBIT = 'tibbit',
    SNIPPET = 'snippet'
}

export enum ValidationMessageType {
    ERROR = 'error',
    WARNING = 'warning',
    MESSAGE = 'message',
    ADD = 'add',
    REMOVE = 'remove',
    UPDATE = 'update',
}

export interface ValidationMessage {
    featureId?: string; // feature that has the error/change
    settingId?: string; // setting that has the error/change
    message: string; // message to display
    messageType: ValidationMessageType;
    data?: any; // for updates this will contain the new value
}

export interface ValidationError {
    [featureID: string]: ValidationErrorMessageMap;
}

export interface ValidationErrorMessageMap {
    [settingID: string]: string;
}

export interface Define {
    name: string;
    value: string;
}

export interface FlowVariable {
    name: string;
    dataType: TibboDataType;
    default?: string;
    byRef?: boolean;
    helpText?: string;
}

export interface FlowEdge {
    name: string;
    /**
     * This has to be globally unique
     */
    id: string;
    location?: string;
    variables: FlowVariable[]
}

export interface FlowLink {
    fromFeature: string;
    fromLocation: string;
    toFeature: string;
    toLocation: string;
}

export const enum TIBBO_PROXY_MESSAGE {
    REFRESH = 'refresh',
    DEVICE = 'device',
    BUZZ = 'buzz',
    REBOOT = 'reboot',
    UPLOAD = 'upload',
    REGISTER = 'register',
    APPLICATION_UPLOAD = 'application',
    UPLOAD_COMPLETE = 'upload_complete',
    STATE = 'state',
    COMMAND = 'command',
    REPLY = 'reply',
    SET_PDB_STORAGE_ADDRESS = 'set_pdb_storage_address',
    DEBUG_PRINT = 'debug_print',
    NETWORKS_UPDATED = 'networks_updated',
    SET_NETWORK = 'set_network',
}

export interface HTTPMessage {
    url: string;
    data: any;
    method: string;
    headers: any;
    nonce?: string;
}

export enum PCODE_COMMANDS {
    STATE = 'PC',
    RUN = 'PR',
    PAUSE = 'PB',
    BREAKPOINT = 'CB',
    GET_MEMORY = 'GM',
    GET_PROPERTY = 'GP',
    SET_PROPERTY = 'SR',
    SET_MEMORY = 'SM',
    STEP = 'PO',
    SET_POINTER = 'SP',
    DISCOVER = '_?',
    INFO = 'X',
    RESET_PROGRAMMING = 'Q',
    UPLOAD = 'D',
    APPUPLOADFINISH = 'T',
    BUZZ = 'B',
    REBOOT = 'E'
}

export enum DEBUG_STATE {
    BUILDING = 'BUILDING',
    DEBUG = 'DEBUG',
    UPLOADING = 'UPLOADING',
}

export interface TaikoMessage {
    mac: string;
    command: PCODE_COMMANDS;
    data: string;
    nonce?: string;
}

export interface TOBJLineInfo {
    line: number,
    address: number
}

export interface TibboDeviceState {
    state: PCODEMachineState;
    registerA: number;
    registerB: number;
    pcodeCounter: number;
    stackPointer: number;
    flags: string;
}

export enum PCODEMachineState {
    STOPPED = '***',
    RUN = '*R*',
    PAUSED = '**B',
    DEBUG_PRINT_AND_STOP = '**P',
    DEBUG_PRINT_AND_CONTINUE = '*P*'
}

export enum TOBJ_DATA_TYPES {
    TOBJ_TYPE_UNKNOWN = 0,
    TOBJ_TYPE_BOOLEAN = 1,
    TOBJ_TYPE_BYTE = 2,
    TOBJ_TYPE_CHAR = 3,
    TOBJ_TYPE_WORD = 4,
    TOBJ_TYPE_SHORT = 5,
    TOBJ_TYPE_DWORD = 6,
    TOBJ_TYPE_LONG = 7,
    TOBJ_TYPE_REAL = 8,
    TOBJ_TYPE_STRING = 9,
    TOBJ_TYPE_ENUM = 10,
    TOBJ_TYPE_ARRAY = 11,
    TOBJ_TYPE_STRUCT = 12,
    TOBJ_TYPE_VOID = 13,
    TOBJ_TYPE_BITFIELD = 14,
    TOBJ_TYPE_POINTER = 15,
    TOBJ_TYPE_REFERENCE = 16,
    TOBJ_TYPE_ARRAY_C = 17,
    TOBJ_TYPE_STRUCT_C = 18,
    TOBJ_TYPE_UNION = 19,
}

export interface TOBJVariableEntry {
    flags: TOBJVariableFlags,
    name: string,
    address: TOBJAddressEntry,
    value?: string,
    ownerScope: TOBJScopeEntry,
    dataType: TOBJDataType
}

export interface TOBJVariableFlags {
    isArgument: boolean,
    isByref: boolean,
    isTemporary: boolean,
    isStatic: boolean
}

export interface TOBJAddressEntry {
    /**
     * Combination of TOBJ_ADDRESS_FLAGS
     */
    flags: TOBJAddressFlags
    /**
     * Address tag (e.g., the mangled name of a function)
     */
    tag: number
    /**
     * Depending on m_Flags, this is an address in TOBJ_SECTION_CODE, TOBJ_SECTION_INIT, or RAM
     */
    dwAddress: number
    /**
     * If this is a relative address (e.g., the address of a struct field)
     * then m_dwBaseAddress holds index of TOBJ_ADDRESS_ENTRY of the parent address);
     * otherwise, it’s set to 0xffffffff
     */
    baseAddress: number
    /**
     * Number of references to this address
     */
    refCount: number
    references: Array<TOJBReferenceEntry>
}

export interface TOBJScopeEntry {
    begin: TOBJSourceAnchor,
    end: TOBJSourceAnchor
}

export interface TOJBReferenceEntry {
    referenceType: TOBJ_REFERENCE_TYPES,
    referencedFrom: number
}

export interface TOBJDataType {
    dataType: TOBJ_DATA_TYPES,
    typeDescriptionInline?: number,
    typeDescriptionIndex: number,
    typeDescription?: TOBJTypeEntry
}

export interface TOBJAddressFlags {
    isDefined: boolean
    isPublic: boolean
    isCode: boolean
    isInit: boolean
}

export interface TOBJSourceAnchor {
    sourcePosition: TOBJSourcePosition,
    address: number
}

export enum TOBJ_REFERENCE_TYPES {
    TOBJ_REF_CODE = 0,
    TOBJ_REF_INIT = 1,
    TOBJ_REF_HTML = 2
}

export interface TOBJTypeEntry {
    dataType: TOBJ_DATA_TYPES,
    name: string,
    size: number,
    elementCount: number, // For arrays, it’s the number of elements;
    // for enums, structs & unions, it’s the number of member fields.
    referenceCount: number, // Number of references to this typedesc
    referenceDataType?: TOBJDataType,
    enumEntries: Array<TOBJEnumMember>,
    structEntries: Array<TOBJStructMember>,
}

export interface TOBJSourcePosition {
    fileName: string,
    line: number,
    column: number
}

export interface TOBJEnumMember {
    baseType: TOBJ_DATA_TYPES,
    name: string,
    value: number
}

export interface TOBJStructMember {
    name: string,
    dataType: TOBJDataType,
    offset: number
}

export interface TOBJPropertyEntry {
    name: string,
    syscallGet: number,
    syscallSet: number,
    dataType: TOBJDataType
}

export const TOBJ_DATA_TYPES_NAMES = [
    'unknown',
    'boolean',
    'byte',
    'char',
    'word',
    'short',
    'dword',
    'long',
    'real',
    'string',
    'enum',
    'array',
    'struct',
    'void',
    'bitfield',
    'pointer',
    'reference',
    'array_c',
    'struct_c',
    'union',
];

export interface TOBJFunctionEntry {
    flags: TOBJFunctionFlags,
    name: string,
    address: TOBJAddressEntry,
    eventIndex: number,
    calleeIndices: Array<number>,
    callees: Array<TOBJFunctionEntry>
}

export interface TOBJFunctionFlags {
    isEvent: boolean,
    doevents: boolean,
    isHtml: boolean,
    isStatic: boolean,
}

export enum FILE_SIGNATURE {
    OBJ_FILE_SIGNATURE = 'TOBJ',
    PDB_FILE_SIGNATURE = 'TPDB',
    BIN_FILE_SIGNATURE = 'TBIN'
}

export interface TOBJSectionDescriptor {
    name: TOBJ_SECTION_DESCRIPTOR;
    offset: number;
    size: number;
    buffer?: Buffer;
    contents?: string;
}

export enum TOBJ_SECTION_DESCRIPTOR {
    TOBJ_SECTION_CODE = 0,
    TOBJ_SECTION_INIT = 1,
    TOBJ_SECTION_RDATA = 2,
    TOBJ_SECTION_FILE_DATA = 3,
    TOBJ_SECTION_SYMBOLS = 4,
    TOBJ_SECTION_RES_FILE_DIR = 5,
    TOBJ_SECTION_EVENT_DIR = 6,
    TOBJ_SECTION_LIB_FILE_DIR = 7,
    TOBJ_SECTION_EXTRA = 8,
    TOBJ_SECTION_ADDRESSES = 9,
    TOBJ_SECTION_FUNCTIONS = 10,
    TOBJ_SECTION_SCOPES = 11,
    TOBJ_SECTION_VARIABLES = 12,
    TOBJ_SECTION_OBJECTS = 13,
    TOBJ_SECTION_SYSCALLS = 14,
    TOBJ_SECTION_TYPES = 15,
    TOBJ_SECTION_RDATA_DIR = 16,
    TOBJ_SECTION_LINE_INFO = 17,
    TOBJ_SECTION_LIB_NAME_DIR = 18,
    TOBJ_SECTION_INC_NAME_DIR = 19,
}

export enum TimeFormats {
    twelveHour = 'hh:mm:ss tt',
    twentyfourHour = 'HH:mm:ss',
}

export interface TOBJSourceFile {
    fileName: string,
    lineCount: number,
    lines: Array<TOBJLineInfo>
}

export interface TOBJObjectEntry {
    name: string,
    properties: Array<TOBJPropertyEntry>
}

export interface TibboDevice {
    ip: string;
    mac: string;
    messageQueue: Array<TaikoMessage>;
    tios: string;
    app: string;
    file?: Buffer;
    fileIndex: number;
    fileBlocksTotal: number;
    pcode: PCODE_STATE;
    lastRunCommand?: TaikoMessage;
    state: PCODEMachineState;
    pdbStorageAddress?: number;
}

export enum PCODE_STATE {
    STOPPED = 0,
    PAUSED = 1,
    RUNNING = 2
}

export interface AppBlockLinkParameter {
    name: string,
    description?: string,
    dataType: TibboDataType,
    default?: string,
    max?: number,
    min?: number,
    regex?: RegExp,
}

export interface AppBlockPort {
    name: string,
    portId: string,
    label?: string,
    params: AppBlockLinkParameter[]
}

export class ProjectCompileError extends Error {
    message: string;

    data: any;

    constructor({
        message,
        data,
    } : {
        message: string,
        data: any,
    }) {
        super();
        this.message = message;
        this.data = data;
    }
}

export interface LayoutElement {
    type: 'rectangle' | 'circle' | 'triangle' | 'point' | 'label';
    x: number;
    y: number;
    color: string;
}

export interface LayoutRectangle extends LayoutElement {
    type: 'rectangle';
    label: string;
    width: number;
    height: number;
    labelSize?: number;
    fillColor?: string;
    strokeWidth?: number;
    labelColor?: string;
}

export interface LayoutCircle extends LayoutElement {
    type: 'circle';
    radius: number;
    label?: string;
    labelSize?: number;
    fillColor?: string;
    strokeWidth?: number;
    labelColor?: string;
}

export interface LayoutTriangle extends LayoutElement {
    type: 'triangle';
    width: number;
    height: number;
    label?: string;
    labelSize?: number;
    fillColor?: string;
    strokeWidth?: number;
    labelColor?: string;
}

export interface LayoutPoint extends LayoutElement {
    type: 'point';
    radius: number;
    id: string;
    target: string;
    connectionLabel?: string | null;
    name?: string;
}

export interface LayoutLabel extends LayoutElement {
    type: 'label';
    text: string;
    fontSize?: number;
}

export type AnyLayoutElement =
    | LayoutRectangle
    | LayoutCircle
    | LayoutTriangle
    | LayoutPoint
    | LayoutLabel;
